import { axiosAuthed } from '../../../shared/utils/setCommonHeaders';
import {
  FETCH_DAILY_BRIEF_START,
  FETCH_DAILY_BRIEF_SUCCESS,
  FETCH_DAILY_BRIEF_ERROR,
  LOAD_MORE_DAILY_BRIEF_START,
  LOAD_MORE_DAILY_BRIEF_SUCCESS,
  LOAD_MORE_DAILY_BRIEF_ERROR,
  FETCH_SETTINGS_START,
  FETCH_SETTINGS_SUCCESS,
  UPDATE_SETTINGS_START,
  UPDATE_SETTINGS_SUCCESS,
  UPDATE_SETTINGS_ERROR,
  FETCH_FILTER_SELECTIONS_START,
  FETCH_FILTER_SELECTIONS_SUCCESS,
  FETCH_FILTER_SELECTIONS_ERROR,
  SET_FILTER_TYPE,
  SET_FILTER_IDS,
  SET_SORT_BY,
  SET_SORT_DIRECTION,
  SET_PAGE,
  SET_PAGE_SIZE,
  RESET_FILTERS,
  RESET_DAILY_BRIEF_DATA,
} from './actionTypes';

const DEFAULT_SETTINGS = {
  defaultFilterType: null, // null = all users (no filter), 2 = Group (single select only)
  defaultFilterIds: null, // Single ID or null for no filter
  defaultSortBy: 'name', // 'alertCount', 'completionPct', 'name', 'remainingDays'
  defaultSortOrder: 'asc', // 'asc' or 'desc'
};

const appendQueryParams = (queryParams, {
  sortBy, sortDir, filterType, filterIds,
}) => {
  if (sortBy) {
    queryParams.append('sortBy', sortBy);
  }
  if (sortDir) {
    queryParams.append('sortDir', sortDir);
  }

  if (filterType && filterIds.length > 0) {
    queryParams.append('filterType', filterType);
    queryParams.append('filterIds', filterIds.join(','));
  }
};

const isNoResultsError = (error) => {
  const errorMessage = error.response?.headers?.message
    || error.response?.data?.message
    || '';
  return error.response?.status === 400
    && (errorMessage.includes('No users found')
    || errorMessage.includes('no athletes associated with the submitted group'));
};

// Data Actions
export const fetchDailyBrief = (accountCode, params = {}) => (dispatch, getState) => {
  const {
    filterType,
    filterIds = [],
    sortBy = 'name',
    sortDir = 'asc',
    page = 1,
    pageSize = 20,
  } = params;

  // Create cache key from params
  const cacheKey = `${filterType}-${filterIds.join(',')}-${sortBy || 'none'}-${sortDir || 'none'}-${page}-${pageSize}`;

  // Check if we have cached data that's still fresh (5 seconds)
  const state = getState();
  const {
    lastFetch,
    cacheKey: currentCacheKey,
    data,
    totalCount,
    hasMoreData,
    hasAssignments,
    hasQuestionnaireAlerts,
  } = state.dailyBrief.data;
  const isCacheFresh = lastFetch && (Date.now() - lastFetch < 5000);

  // Skip fetch if cache is fresh and params haven't changed
  if (isCacheFresh && currentCacheKey === cacheKey) {
    // Dispatch success with cached data to ensure loading state is cleared
    dispatch({
      type: FETCH_DAILY_BRIEF_SUCCESS,
      payload: {
        data,
        totalCount,
        page,
        pageSize,
        hasMoreData,
        hasAssignments,
        hasQuestionnaireAlerts,
        cacheKey,
      },
    });
    return Promise.resolve();
  }

  dispatch({ type: FETCH_DAILY_BRIEF_START });

  const queryParams = new URLSearchParams({
    page,
    pageSize,
  });

  appendQueryParams(queryParams, {
    sortBy, sortDir, filterType, filterIds,
  });

  return axiosAuthed
    .get(`/accounts/${accountCode}/reports/daily-brief?${queryParams}`)
    .then((response) => {
      const resp = response.data || {};
      const athletes = Array.isArray(resp.athletes) ? resp.athletes : [];
      const assignments = typeof resp.hasAssignments === 'boolean' ? resp.hasAssignments : null;
      const questionnaireAlerts = typeof resp.hasQuestionnaireAlerts === 'boolean' ? resp.hasQuestionnaireAlerts : null;
      const moreData = athletes.length === pageSize;
      dispatch({
        type: FETCH_DAILY_BRIEF_SUCCESS,
        payload: {
          data: athletes,
          totalCount: athletes.length,
          page,
          pageSize,
          hasMoreData: moreData,
          hasAssignments: assignments,
          hasQuestionnaireAlerts: questionnaireAlerts,
          cacheKey,
        },
      });
    })
    .catch((error) => {
      if (isNoResultsError(error)) {
        dispatch({
          type: FETCH_DAILY_BRIEF_SUCCESS,
          payload: {
            data: [],
            totalCount: 0,
            page,
            pageSize,
            hasMoreData: false,
            hasAssignments: null,
            hasQuestionnaireAlerts: null,
            cacheKey,
          },
        });
      } else {
        dispatch({
          type: FETCH_DAILY_BRIEF_ERROR,
          payload: error.response?.headers?.message || error.response?.data || error.message,
        });
      }
    });
};

export const loadMoreDailyBrief = (accountCode, params = {}) => (dispatch, getState) => {
  const state = getState();
  const currentPage = state.dailyBrief.data.page;
  const currentPageSize = state.dailyBrief.data.pageSize;

  const {
    filterType,
    filterIds = [],
    sortBy = 'name',
    sortDir = 'asc',
  } = params;

  const nextPage = currentPage + 1;

  dispatch({ type: LOAD_MORE_DAILY_BRIEF_START });

  const queryParams = new URLSearchParams({
    page: nextPage,
    pageSize: currentPageSize,
  });

  appendQueryParams(queryParams, {
    sortBy, sortDir, filterType, filterIds,
  });

  return axiosAuthed
    .get(`/accounts/${accountCode}/reports/daily-brief?${queryParams}`)
    .then((response) => {
      const resp = response.data || {};
      const athletes = Array.isArray(resp.athletes) ? resp.athletes : [];
      const assignments = typeof resp.hasAssignments === 'boolean' ? resp.hasAssignments : null;
      const questionnaireAlerts = typeof resp.hasQuestionnaireAlerts === 'boolean' ? resp.hasQuestionnaireAlerts : null;
      const moreData = athletes.length === currentPageSize;
      dispatch({
        type: LOAD_MORE_DAILY_BRIEF_SUCCESS,
        payload: {
          data: athletes,
          page: nextPage,
          hasMoreData: moreData,
          hasAssignments: assignments,
          hasQuestionnaireAlerts: questionnaireAlerts,
        },
      });
    })
    .catch((error) => {
      if (isNoResultsError(error)) {
        dispatch({
          type: LOAD_MORE_DAILY_BRIEF_SUCCESS,
          payload: {
            data: [],
            page: nextPage,
            hasMoreData: false,
            hasAssignments: null,
            hasQuestionnaireAlerts: null,
          },
        });
      } else {
        dispatch({
          type: LOAD_MORE_DAILY_BRIEF_ERROR,
          payload: error.response?.headers?.message || error.response?.data || error.message,
        });
      }
    });
};

export const fetchSettings = (accountCode) => (dispatch) => {
  dispatch({ type: FETCH_SETTINGS_START });

  return axiosAuthed
    .get(`/me/accounts/${accountCode}/settings/daily-brief-report`)
    .then((response) => {
      const settings = {
        defaultFilterType: response.data?.defaultFilterType ?? null,
        defaultFilterIds: response.data?.defaultFilterIds ?? null,
        defaultSortBy: response.data?.defaultSortBy ?? 'name',
        defaultSortOrder: response.data?.defaultSortOrder ?? 'asc',
      };
      dispatch({
        type: FETCH_SETTINGS_SUCCESS,
        payload: settings,
      });
    })
    .catch(() => {
      dispatch({
        type: FETCH_SETTINGS_SUCCESS,
        payload: DEFAULT_SETTINGS,
      });
    });
};

export const updateSettings = (accountCode, settings) => (dispatch) => {
  dispatch({ type: UPDATE_SETTINGS_START });

  return axiosAuthed
    .put(`/me/accounts/${accountCode}/settings/daily-brief-report`, settings)
    .then((response) => {
      dispatch({
        type: UPDATE_SETTINGS_SUCCESS,
        payload: response.data,
      });
    })
    .catch((error) => {
      dispatch({
        type: UPDATE_SETTINGS_ERROR,
        payload: error.response?.data || error.message,
      });
      throw error;
    });
};

// Filter Selections Actions
export const fetchFilterSelections = (accountCode, filterType) => (dispatch) => {
  dispatch({ type: FETCH_FILTER_SELECTIONS_START });

  const queries = {
    1: 'users?userType=0&groupFilter=reporting',
    2: 'groups?reporting=1',
    3: 'calendars?archived=0',
  };

  if (!queries[filterType]) {
    dispatch({
      type: FETCH_FILTER_SELECTIONS_ERROR,
      payload: `Invalid filterType: ${filterType}`,
    });
    return Promise.reject(new Error(`Invalid filterType: ${filterType}`));
  }

  return axiosAuthed
    .get(`/accounts/${accountCode}/${queries[filterType]}`)
    .then((response) => {
      dispatch({
        type: FETCH_FILTER_SELECTIONS_SUCCESS,
        payload: response.data,
      });
    })
    .catch((error) => {
      dispatch({
        type: FETCH_FILTER_SELECTIONS_ERROR,
        payload: error.response?.data || error.message,
      });
    });
};

// UI Actions
export const setFilterType = (filterType) => ({
  type: SET_FILTER_TYPE,
  payload: filterType,
});

export const setFilterIds = (filterIds) => ({
  type: SET_FILTER_IDS,
  payload: filterIds,
});

export const setSortBy = (sortBy) => ({
  type: SET_SORT_BY,
  payload: sortBy,
});

export const setSortDirection = (sortDirection) => ({
  type: SET_SORT_DIRECTION,
  payload: sortDirection,
});

export const setPage = (page) => ({
  type: SET_PAGE,
  payload: page,
});

export const setPageSize = (pageSize) => ({
  type: SET_PAGE_SIZE,
  payload: pageSize,
});

export const resetFilters = () => ({
  type: RESET_FILTERS,
});

export const resetDailyBriefData = () => ({
  type: RESET_DAILY_BRIEF_DATA,
});
